import Divider from 'LuxKit/Divider'
import Group from 'components/utils/Group'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import config from 'constants/config'
import { useAppDispatch } from 'hooks/reduxHooks'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import IconWrapper from 'LuxKit/Icons/IconWrapper'
import { getPlural } from 'lib/string/pluralize'
import fetchAccountBusiness from 'actions/businessTraveller/fetchBusinessAccount'
import useEmployeeOrders from 'businessTraveller/hooks/useEmployeeOrders'
import LineBuildingIcon from 'LuxKit/Icons/line/LineBuildingIcon'
import BusinessTravellerAccountGuard from '../BusinessTravellerAccountGuard'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { authLogout } from 'actions/AuthActions'
import BusinessTravellerAccountListItemFailedAccess from './BusinessTravellerAccountListItemFailedAccess'
import ListItemLoadingSkeleton from 'LuxKit/List/ListItemLoadingSkeleton'
import { clearBusinessTravellerSelectedTraveller } from 'businessTraveller/storage/businessTravellerStorage'
import GeoContext from 'contexts/geoContext'
import useBusinessTravellerTripRequestApprovals from 'businessTraveller/hooks/useBusinessTravellerTripRequestApprovals'
import useBusinessTravellerTripRequests from 'businessTraveller/hooks/useBusinessTravellerTripRequests'
import { BUSINESS_TRAVELLER_MY_APPROVALS_PENDING_ROUTE, BUSINESS_TRAVELLER_MY_TRIP_REQUESTS_ACTIVE_ROUTE } from 'components/App/Routes/routeConstants'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ListItem from 'LuxKit/List/ListItem'
import LineFileQuestionAltIcon from 'LuxKit/Icons/line/LineFileQuestionAltIcon'
import LineBagAltIcon from 'LuxKit/Icons/line/LineBagAltIcon'
import LineCogIcon from 'LuxKit/Icons/line/LineCogIcon'
import LineSignoutIcon from 'LuxKit/Icons/line/LineSignoutIcon'
import LineUserCircleIcon from 'LuxKit/Icons/line/LineUserCircleIcon'
import LineCreditCardIcon from 'LuxKit/Icons/line/LineCreditCardIcon'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'

interface MappedStateProps {
  currentBusinessAccount?: App.BusinessTraveller.CurrentBusinessAccount
}

interface Props {
  onListItemClick?: () => void
}

function BusinessTravellerAccountMenuContent(props: Props & MappedStateProps) {
  const {
    currentBusinessAccount,
    onListItemClick,
  } = props
  const dispatch = useAppDispatch()
  const { currentRegionCode } = useContext(GeoContext)

  const employeeId = currentBusinessAccount?.employee?.id

  // fetchAccountBusiness has a guard to skip if the currentBusinessAccount is already set, no duplicate calls here
  useEffect(() => {
    dispatch(fetchAccountBusiness())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { upcoming } = useBusinessTravellerTripRequests({ currentRegionCode, isEnabled: !!employeeId })
  const { pending } = useBusinessTravellerTripRequestApprovals({ currentRegionCode, isEnabled: !!employeeId })

  const { total: numberOfUpcomingOrders } = useEmployeeOrders({
    timeframe: 'upcoming',
    page: 0,
    limit: 1,
  })

  const myOrdersLabel = useMemo<string>(() => {
    let text = `My ${getPlural(config.brandConfig.purchaseSynonym, 2)}`

    if (numberOfUpcomingOrders) {
      text += ` (${numberOfUpcomingOrders} upcoming)`
    }

    return text
  }, [numberOfUpcomingOrders])

  const modalState = useModalElementContext<'item-clicked'>()

  const handleItemClick = useCallback(() => {
    modalState?.resolve('item-clicked')
    onListItemClick?.()
  }, [modalState, onListItemClick])

  const logOut = useCallback(() => {
    // stolen from `AccountLandingPage.tsx`
    config.GOOGLE_ONE_TAP_LOGIN && window.google?.accounts?.id.disableAutoSelect()
    clearBusinessTravellerSelectedTraveller()
    dispatch(authLogout())
    handleItemClick()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <ModalBase mode="drawer-starboard">
    <ModalHeader title="Your account" />
    <ModalBody>
      <ModalContent>
        <Group direction="vertical" gap={8} data-testid="business-traveller-account-menu">
          <BusinessTravellerAccountGuard
            loading={<ListItemLoadingSkeleton startIcon={<IconWrapper className="variant-bubble" size={36}><LineBuildingIcon size="S" /></IconWrapper>} subtitle="100%" />}
            fallback={<BusinessTravellerAccountListItemFailedAccess />}
          >
            <ListItem
              startIcon={<IconWrapper className="variant-bubble" size={36}><LineBuildingIcon size="S" /></IconWrapper>}
              title={`Business · ${currentBusinessAccount?.business?.name}`}
              subtitle={currentBusinessAccount?.employee?.email}
            />
          </BusinessTravellerAccountGuard>
          <Divider kind="primary" />
          <ClickableListItem
            to="/account/details"
            startIcon={<LineUserCircleIcon />}
            title="My Profile"
            onClick={handleItemClick}
          />
          <ClickableListItem
            to="/account/my-bookings"
            startIcon={<LineBagAltIcon />}
            title={myOrdersLabel}
            titleFormat="titlecase"
            onClick={handleItemClick}
          />
          <ClickableListItem
            to={BUSINESS_TRAVELLER_MY_TRIP_REQUESTS_ACTIVE_ROUTE}
            startIcon={<LineFileQuestionAltIcon />}
            title={`My Trip Requests (${upcoming?.length ?? 0})`}
            onClick={handleItemClick}
          />
          <BusinessTravellerAccountGuard
            employeeRoles={['BUSINESS_ADMIN', 'BUSINESS_MANAGER', 'BUSINESS_APPROVER']}
            loading={<ListItemLoadingSkeleton startIcon={<LineFileQuestionAltIcon />} subtitle={false} />}
          >
             <ClickableListItem
               to={BUSINESS_TRAVELLER_MY_APPROVALS_PENDING_ROUTE}
               startIcon={<LineCheckCircleIcon />}
               title={`My Approvals (${pending?.length ?? 0})`}
               onClick={handleItemClick}
             />
          </BusinessTravellerAccountGuard>
          <ClickableListItem
            to="/account/credits-payments"
            startIcon={<LineCreditCardIcon />}
            title="My Credits & Payment Methods"
            onClick={handleItemClick}
          />
          <Divider kind="primary" />
          <BusinessTravellerAccountGuard
            employeeRoles={['BUSINESS_ADMIN', 'BUSINESS_MANAGER']}
            loading={<ListItemLoadingSkeleton startIcon={<LineCogIcon />} subtitle={false} />}
          >
            <ClickableListItem
              href={config.businessTraveller.businessAdminPortalHref}
              target="_blank"
              startIcon={<LineCogIcon />}
              title="Admin Portal"
            />
            <Divider kind="primary" />
          </BusinessTravellerAccountGuard>
          <ClickableListItem
            type="button"
            startIcon={<LineSignoutIcon />}
            title="Log out"
            onClick={logOut}
          />
          <Divider kind="primary" />
        </Group>
      </ModalContent>
    </ModalBody>
  </ModalBase>
}

export default connect<MappedStateProps, undefined, {}, App.State>((state) => {
  return {
    currentBusinessAccount: state.businessTraveller.currentBusinessAccount,
  }
})(BusinessTravellerAccountMenuContent)
